import React, { useState } from 'react';
import API from '../api';

/**
 * The contact form that works without an account.
 *
 * Posts to /api/support/message, which writes the row before it tries to email
 * anyone — so this keeps working when mail is misconfigured, when there is no
 * admin to notify, and when the sender has no account at all. That last case is
 * the reason it exists: the people most likely to need help are the ones who
 * can't sign in, and they are invisible to a DM-only channel.
 *
 * Used in two places, which is why it's a component and not part of the login
 * page: on the login screen, and inside the support modal on the rare occasion
 * there is no contact account to open a thread with.
 */
export default function SupportForm({ defaultEmail = '', onDone, compact = false }) {
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    setSending(true);
    try {
      const res = await API.post('/support/message', { email: email.trim(), message: message.trim() });
      setSent(res.data?.message || "Got it. We'll write back to that address.");
      setMessage('');
      onDone?.();
    } catch (e2) {
      setErr(e2.response?.data?.error || "That didn't send. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div style={{
        background: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.3)',
        borderRadius: 10, padding: '14px 16px', color: '#9ee89e', fontSize: 13, lineHeight: 1.6
      }}>
        ✅ {sent}
      </div>
    );
  }

  const input = {
    width: '100%', boxSizing: 'border-box', padding: '11px 12px',
    borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: 14,
    fontFamily: 'inherit', outline: 'none'
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {!compact && (
        <div style={{ color: '#889', fontSize: 13, lineHeight: 1.6 }}>
          Can't get in, or something isn't working? Tell us here and we'll write
          back. Usually the same day, at the latest the next one.
        </div>
      )}
      <input
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="Your email"
        autoComplete="email"
        style={input}
      />
      <textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        placeholder="What's going on?"
        rows={4}
        maxLength={2000}
        style={{ ...input, resize: 'vertical', minHeight: 90 }}
      />
      {err && <div style={{ color: '#ff8888', fontSize: 13 }}>{err}</div>}
      <button
        type="submit"
        disabled={sending || !email.trim() || !message.trim()}
        style={{
          padding: '12px 20px', borderRadius: 8, border: 'none',
          fontFamily: "'Oswald', sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 1,
          background: sending || !email.trim() || !message.trim()
            ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg,#D50A0A,#a00808)',
          color: sending || !email.trim() || !message.trim() ? '#667' : '#fff',
          cursor: sending || !email.trim() || !message.trim() ? 'default' : 'pointer'
        }}
      >{sending ? 'SENDING…' : 'SEND'}</button>
    </form>
  );
}
